import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, X, Sparkles, Download, Upload, Trash2, Image } from 'lucide-react';
import SEOHead, { seoConfig } from '../components/SEOHead';
import InteriorConstructor from '../components/InteriorConstructor';
import PanelCalculator from '../components/PanelCalculator';
import { NewBadge, isNew } from '../components/NewBadge';
import { API_URL } from '../config';



const CatalogPage = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedTexture, setSelectedTexture] = useState(null);
  const [catalogImages, setCatalogImages] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [uploadingCode, setUploadingCode] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const seo = seoConfig.catalog[language] || seoConfig.catalog.en || seoConfig.catalog.ru;

  // Check admin session
  useEffect(() => {
    const sessionId = localStorage.getItem('admin_session');
    if (sessionId) {
      fetch(`${API_URL}/api/auth/verify-session/${sessionId}`)
        .then(r => r.ok ? setIsAdmin(true) : setIsAdmin(false))
        .catch(() => setIsAdmin(false));
    }
  }, []);

  // Load catalog images
  useEffect(() => {
    fetch(`${API_URL}/api/catalog/images`)
      .then(r => r.json())
      .then(data => {
        const map = {};
        data.forEach(img => { map[`${img.category}_${img.texture_code}`] = img.image_url; });
        setCatalogImages(map);
      })
      .catch(() => {});
  }, []);

  // Read category from URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    if (categoryParam) {
      // Map URL params to category keys
      const categoryMap = {
        'standard': 'standard',
        'premium': 'premium',
        'superpremium': 'superPremium',
        'luxe': 'luxury',
        'luxury': 'luxury',
        'nomad': 'nomad',
        'eastern': 'oriental',
        'oriental': 'oriental',
        'gray': 'grey',
        'grey': 'grey',
        'cashmere': 'cashmere'
      };
      const mappedCategory = categoryMap[categoryParam.toLowerCase()];
      if (mappedCategory) {
        setActiveCategory(mappedCategory);
      }
    }
  }, [location.search]);

  const content = {
    ru: {
      header: {
        title: 'Каталог панелей',
        titleBrand: 'GIPSOVINIL.KZ',
        subtitle: 'Выберите идеальную текстуру для вашего проекта'
      },
      mainCatalog: 'Основной каталог',
      designSolutions: 'Дизайн решения',
      downloadCatalog: 'Скачать каталог панелей',
      downloadOrnaments: 'Скачать каталог орнаментов',
      downloadAuthor: 'Скачать каталог профилей',
      categories: {
        all: 'Все коллекции',
        standard: 'Стандарт',
        premium: 'Премиум',
        superPremium: 'Супер-Премиум',
        luxury: 'Люкс',
        nomad: 'Nomad Panels',
        oriental: 'Восточные вкусы',
        grey: '5 оттенков серого',
        cashmere: 'Кашемир'
      },
      cta: {
        title: 'Нужна помощь в выборе?',
        subtitle: 'Наши специалисты помогут подобрать идеальную текстуру для вашего проекта',
        button: 'Получить консультацию'
      },
      modal: {
        contact: 'Заказать образец'
      }
    },
    kz: {
      header: {
        title: 'Текстуралар каталогы',
        titleBrand: 'GIPSOVINIL.KZ',
        subtitle: 'Жобаңыз үшін тамаша текстураны таңдаңыз'
      },
      mainCatalog: 'Негізгі каталог',
      designSolutions: 'Дизайн шешімдері',
      downloadCatalog: 'Панельдер каталогын жүктеу',
      downloadOrnaments: 'Ою-өрнек каталогын жүктеу',
      downloadAuthor: 'Профильдер каталогын жүктеу',
      categories: {
        all: 'Барлық коллекциялар',
        standard: 'Стандарт',
        premium: 'Премиум',
        superPremium: 'Супер-Премиум',
        luxury: 'Люкс',
        nomad: 'Nomad Panels',
        oriental: 'Шығыс дәмі',
        grey: 'Сұрдың 5 Реңкі',
        cashmere: 'Кашемир'
      },
      cta: {
        title: 'Таңдауда көмек керек пе?',
        subtitle: 'Біздің мамандар жобаңыз үшін тамаша текстура таңдауға көмектеседі',
        button: 'Кеңес алу'
      },
      modal: {
        contact: 'Үлгіге тапсырыс беру'
      }
    },
    en: {
      header: {
        title: 'Texture Catalog',
        titleBrand: 'GIPSOVINIL.KZ',
        subtitle: 'Choose the perfect texture for your project'
      },
      mainCatalog: 'Main Catalog',
      designSolutions: 'Design Solutions',
      downloadCatalog: 'Download Panel Catalog',
      downloadOrnaments: 'Download Ornaments Catalog',
      downloadAuthor: 'Download Profiles Catalog',
      categories: {
        all: 'All Collections',
        standard: 'Standard',
        premium: 'Premium',
        superPremium: 'Super-Premium',
        luxury: 'Luxury',
        nomad: 'Nomad Panels',
        oriental: 'Oriental Flavour',
        grey: '5 Shades of Grey',
        cashmere: 'Cashmere'
      },
      cta: {
        title: 'Need help choosing?',
        subtitle: 'Our specialists will help you find the perfect texture for your project',
        button: 'Get Consultation'
      },
      modal: {
        contact: 'Order Sample'
      }
    },
    ar: {
      header: {
        title: 'كتالوج الأنسجة',
        titleBrand: 'GIPSOVINIL.KZ',
        subtitle: 'اختر النسيج المثالي لمشروعك'
      },
      mainCatalog: 'الكتالوج الرئيسي',
      designSolutions: 'حلول تصميمية',
      downloadCatalog: 'تحميل كتالوج الألواح',
      downloadOrnaments: 'تحميل كتالوج الزخارف',
      downloadAuthor: 'تحميل كتالوج البروفيلات',
      categories: {
        all: 'جميع المجموعات',
        standard: 'ستاندرد',
        premium: 'بريميوم',
        superPremium: 'سوبر بريميوم',
        luxury: 'فاخر',
        nomad: 'ألواح نوماد',
        oriental: 'نكهة شرقية',
        grey: '5 درجات من الرمادي',
        cashmere: 'كاشمير'
      },
      cta: {
        title: 'هل تحتاج إلى مساعدة في الاختيار؟',
        subtitle: 'سيساعدك متخصصونا في إيجاد النسيج المثالي لمشروعك',
        button: 'احصل على استشارة'
      },
      modal: {
        contact: 'طلب عينة'
      }
    }
  };

  // Exclusive Collections
  const exclusiveCollections = {
    nomad: {
      name: 'Nomad Panels',
      description: {
        ru: 'Коллекция панелей с казахскими национальными орнаментами - 13 уникальных дизайнов в 7 цветах',
        kz: 'Қазақ ұлттық ою-өрнектері бар панельдер коллекциясы - 7 түсте 13 бірегей дизайн',
        en: 'Collection of panels with Kazakh national ornaments - 13 unique designs in 7 colors', ar: 'مجموعة ألواح بالزخارف الوطنية الكازاخية - 13 تصميماً فريداً في 7 ألوان'
      },
      pdfUrl: '/pdf/katalog-ornamentov-gipsovinil.pdf',
      ornaments: [
        { name: { ru: 'Дыхание степи 1', kz: 'Дала демі 1', en: 'Breath of Steppe 1', ar: 'نسيم السهب 1' }, code: 'NMD-DS1' },
        { name: { ru: 'Дыхание степи 2', kz: 'Дала демі 2', en: 'Breath of Steppe 2', ar: 'نسيم السهب 2' }, code: 'NMD-DS2' },
        { name: { ru: 'Өркен 1', kz: 'Өркен 1', en: 'Orken 1', ar: 'أوركن 1' }, code: 'NMD-OR1' },
        { name: { ru: 'Өркен 2', kz: 'Өркен 2', en: 'Orken 2', ar: 'أوركن 2' }, code: 'NMD-OR2' },
        { name: { ru: 'Өркен 3', kz: 'Өркен 3', en: 'Orken 3', ar: 'أوركن 3' }, code: 'NMD-OR3' },
        { name: { ru: 'Береке', kz: 'Береке', en: 'Bereke', ar: 'بيريكي' }, code: 'NMD-BRK' },
        { name: { ru: 'Қошқар-мүйіз', kz: 'Қошқар-мүйіз', en: 'Koshkar-Muyiz', ar: 'كوشكار مويز' }, code: 'NMD-KM' },
        { name: { ru: 'Арқар мүйіз 1', kz: 'Арқар мүйіз 1', en: 'Arkar Muyiz 1', ar: 'أركار مويز 1' }, code: 'NMD-AM1' },
        { name: { ru: 'Арқар мүйіз 2', kz: 'Арқар мүйіз 2', en: 'Arkar Muyiz 2', ar: 'أركار مويز 2' }, code: 'NMD-AM2' },
        { name: { ru: 'Ветер', kz: 'Жел', en: 'Wind', ar: 'الريح' }, code: 'NMD-VTR' },
        { name: { ru: 'Двойной архар', kz: 'Қос арқар', en: 'Double Arkhar', ar: 'أركار مزدوج' }, code: 'NMD-DA' },
        { name: { ru: 'Орда', kz: 'Орда', en: 'Orda', ar: 'أوردا' }, code: 'NMD-ORD' },
        { name: { ru: 'Крыло птицы', kz: 'Құс қанаты', en: 'Bird Wing', ar: 'جناح الطائر' }, code: 'NMD-KP' }
      ],
      colors: [
        { name: { ru: 'Бархатная халва', kz: 'Барқыт халуа', en: 'Velvet Halva', ar: 'حلوى مخملية' }, code: 'BH', color: '#b6b0a2' },
        { name: { ru: 'Нежная голубика', kz: 'Нәзік көк жидек', en: 'Tender Blueberry', ar: 'توت بري ناعم' }, code: 'NG', color: '#8599b4' },
        { name: { ru: 'Спелый гранат', kz: 'Піскен анар', en: 'Ripe Pomegranate', ar: 'رمان ناضج' }, code: 'SG', color: '#65272f' },
        { name: { ru: 'Душистая зелень', kz: 'Хош иісті жасылдық', en: 'Fragrant Greens', ar: 'خضرة عطرة' }, code: 'DZ', color: '#2d4434' },
        { name: { ru: 'Чай с молоком', kz: 'Сүтті шай', en: 'Tea with Milk', ar: 'شاي بالحليب' }, code: 'CH', color: '#cdb9a6' },
        { name: { ru: 'Свежая черника', kz: 'Жаңа қара жидек', en: 'Fresh Blueberry', ar: 'توت بري طازج' }, code: 'SC', color: '#3d4551' },
        { name: { ru: 'Золотое солнце', kz: 'Алтын күн', en: 'Golden Sun', ar: 'شمس ذهبية' }, code: 'ZS', color: '#d4a849' }
      ]
    },
    oriental: {
      name: 'Восточные вкусы',
      description: {
        ru: 'Восточные вкусы - 16 оттенков, вдохновлённых теплом и гостеприимством Востока',
        kz: 'Шығыс дәмі - Шығыстың жылуы мен қонақжайлылығынан шабыт алған 16 реңк',
        en: 'Oriental Flavour - 16 shades inspired by the warmth and hospitality of the East', ar: '16 درجة مستوحاة من دفء وكرم الشرق - نكهة شرقية'
      },
      textures: [
        { name: { ru: 'Солёный курт', kz: 'Тұзды құрт', en: 'Salty Kurt', ar: 'كورت مالح' }, code: 'EAST-01-SK', color: '#f1ece1' },
        { name: { ru: 'Сгущённое молоко', kz: 'Қоймалжыңдалған сүт', en: 'Condensed Milk', ar: 'حليب مكثف' }, code: 'EAST-02-SM', color: '#e6e1cd' },
        { name: { ru: 'Миндальный крем', kz: 'Бадам кремі', en: 'Almond Cream', ar: 'كريمة اللوز' }, code: 'EAST-04-MK', color: '#e5e2d8' },
        { name: { ru: 'Бархатная халва', kz: 'Барқыт халуа', en: 'Velvet Halva', ar: 'حلوى مخملية' }, code: 'EAST-05-BH', color: '#b6b0a2' },
        { name: { ru: 'Чай с молоком', kz: 'Сүтті шай', en: 'Tea with Milk', ar: 'شاي بالحليب' }, code: 'EAST-06-CH', color: '#cdb9a6' },
        { name: { ru: 'Кофе с молоком', kz: 'Сүтті кофе', en: 'Coffee with Milk', ar: 'قهوة بالحليب' }, code: 'EAST-07-KM', color: '#af9e93' },
        { name: { ru: 'Пряная куркума', kz: 'Дәмді сарымсақ', en: 'Spicy Turmeric', ar: 'كركم حار' }, code: 'EAST-08-PK', color: '#faaf49' },
        { name: { ru: 'Ароматный шафран', kz: 'Хош иісті загфиран', en: 'Aromatic Saffron', ar: 'زعفران عطري' }, code: 'EAST-09-AS', color: '#e57c49' },
        { name: { ru: 'Нежная голубика', kz: 'Нәзік көк жидек', en: 'Tender Blueberry', ar: 'توت بري ناعم' }, code: 'EAST-10-NG', color: '#8599b4' },
        { name: { ru: 'Фисташковая паста', kz: 'Пісте пастасы', en: 'Pistachio Paste', ar: 'عجينة الفستق' }, code: 'EAST-11-FP', color: '#b2ab68' },
        { name: { ru: 'Лазурный зефир', kz: 'Көгілдір зефир', en: 'Azure Zephyr', ar: 'زفير أزرق سماوي' }, code: 'EAST-12-LZ', color: '#cddfe1' },
        { name: { ru: 'Клубничный зефир', kz: 'Құлпынай зефирі', en: 'Strawberry Zephyr', ar: 'زفير الفراولة' }, code: 'EAST-13-KZ', color: '#eed1d3' },
        { name: { ru: 'Свежая черника', kz: 'Жаңа қара жидек', en: 'Fresh Blueberry', ar: 'توت بري طازج' }, code: 'EAST-14-SC', color: '#3d4551' },
        { name: { ru: 'Спелый гранат', kz: 'Піскен анар', en: 'Ripe Pomegranate', ar: 'رمان ناضج' }, code: 'EAST-15-SG', color: '#65272f' },
        { name: { ru: 'Душистая зелень', kz: 'Хош иісті жасылдық', en: 'Fragrant Greens', ar: 'خضرة عطرة' }, code: 'EAST-16-DZ', color: '#2d4434' }
      ]
    },
    grey: {
      name: '5 оттенков серого',
      description: {
        ru: '5 оттенков серого - современная классика для любого интерьера',
        kz: 'Сұрдың 5 реңкі - кез келген интерьерге арналған заманауи классика',
        en: '5 Shades of Grey - modern classics for any interior', ar: '5 درجات من الرمادي - كلاسيكيات عصرية لأي ديكور داخلي'
      },
      textures: [
        { name: { ru: 'Беж грей', kz: 'Беж сұр', en: 'Beige Grey', ar: 'بيج رمادي' }, code: 'GRAY-01-BG', color: '#b2aea5' },
        { name: { ru: 'Лайт грей', kz: 'Ашық сұр', en: 'Light Grey', ar: 'رمادي فاتح' }, code: 'GRAY-02-LG', color: '#c8c8c7' },
        { name: { ru: 'Тотал грей', kz: 'Толық сұр', en: 'Total Grey', ar: 'رمادي شامل' }, code: 'GRAY-03-TG', color: '#a4a8a9' },
        { name: { ru: 'Дарк грей', kz: 'Қою сұр', en: 'Dark Grey', ar: 'رمادي داكن' }, code: 'GRAY-04-DG', color: '#747774' },
        { name: { ru: 'Графит грей', kz: 'Графит сұр', en: 'Graphite Grey', ar: 'رمادي جرافيت' }, code: 'GRAY-05-GG', color: '#55585c' }
      ]
    }
  };

  // Standard catalog data
  const catalogData = {
    standard: [
      { name: { ru: 'Бетон Белый', kz: 'Бетон Ақ', en: 'Concrete White', ar: 'خرسانة بيضاء' }, code: '105021', color: '#f5f5f5' },
      { name: { ru: 'Бетон Светло Серый', kz: 'Бетон Ашық Сұр', en: 'Concrete Light Grey', ar: 'خرسانة رمادي فاتح' }, code: '105002', color: '#d4d4d4' },
      { name: { ru: 'Бетон Темно Серый', kz: 'Бетон Қою Сұр', en: 'Concrete Dark Grey', ar: 'خرسانة رمادي داكن' }, code: '105001', color: '#6b6b6b' },
      { name: { ru: 'Лён Натуральный', kz: 'Табиғи Зығыр', en: 'Natural Linen', ar: 'كتان طبيعي' }, code: 'FS119007-134', color: '#d8d3c5', image: '/images/catalog/standard_FS119007-134.png', newUntil: '2026-12-14' },
      { name: { ru: 'Дуб Натуральный', kz: 'Табиғи Емен', en: 'Natural Oak', ar: 'بلوط طبيعي' }, code: 'FK16166-988', color: '#b09a7b', image: '/images/catalog/standard_FK16166-988.png', newUntil: '2026-12-14' },
      { name: { ru: 'Дуб Шоколадный', kz: 'Шоколадты Емен', en: 'Chocolate Oak', ar: 'بلوط الشوكولاتة' }, code: 'FK16167-988', color: '#6b5a44', image: '/images/catalog/standard_FK16167-988.png', newUntil: '2026-12-14' }
    ],
    cashmere: [
      { name: { ru: 'Нежные облака', kz: 'Нәзік бұлттар', en: 'Gentle Clouds', ar: 'غيوم ناعمة' }, code: 'F422721-804-P5', color: '#f2ece0', newUntil: '2026-12-14' },
      { name: { ru: 'Пудровый рассвет', kz: 'Пудра таңы', en: 'Powder Dawn', ar: 'فجر بودري' }, code: 'F61466-804-P5', color: '#e6e4d7', newUntil: '2026-12-14' },
      { name: { ru: 'Дикая степь', kz: 'Жабайы дала', en: 'Wild Steppe', ar: 'السهب البري' }, code: 'F7320-804-P5', color: '#797546', newUntil: '2026-12-14' },
      { name: { ru: 'Хвойный лес', kz: 'Қылқан жапырақты орман', en: 'Coniferous Forest', ar: 'غابة صنوبرية' }, code: 'F7321-804-P5', color: '#294a37', newUntil: '2026-12-14' },
      { name: { ru: 'Ночное небо', kz: 'Түнгі аспан', en: 'Night Sky', ar: 'سماء الليل' }, code: 'F91191-804-P5', color: '#595b62', newUntil: '2026-12-14' },
      { name: { ru: 'Лёгкий туман', kz: 'Жеңіл тұман', en: 'Light Mist', ar: 'ضباب خفيف' }, code: 'F91192-804-P5', color: '#c9c9c7', newUntil: '2026-12-14' },
      { name: { ru: 'Дымчатое небо', kz: 'Түтінді аспан', en: 'Smoky Sky', ar: 'سماء دخانية' }, code: 'F91222-804-P5', color: '#939491', newUntil: '2026-12-14' },
      { name: { ru: 'Светлое небо', kz: 'Ашық аспан', en: 'Bright Sky', ar: 'سماء مشرقة' }, code: 'F91221-804-P5', color: '#e2e1dd', newUntil: '2026-12-14' }
    ],
    premium: [
      { name: { ru: 'Штукатурка Капучино', kz: 'Сылақ Капучино', en: 'Plaster Cappuccino', ar: 'جص كابتشينو' }, code: 'LS 1923-2-L', color: '#c9b8a5' },
      { name: { ru: 'Штукатурка Лайт Грей', kz: 'Сылақ Ашық Сұр', en: 'Plaster Light Grey', ar: 'جص رمادي فاتح' }, code: 'LS 1922-2-L', color: '#c8c8c8' },
      { name: { ru: 'Шпон Орех Натуральный Софт', kz: 'Жаңғақ Табиғи Жұмсақ', en: 'Natural Walnut Veneer Soft', ar: 'قشرة الجوز الطبيعي الناعم' }, code: 'ZB 254-SFT', color: '#6b5344' },
      { name: { ru: 'Дуб Бомонт Натуральный', kz: 'Бомонт Еменi Табиғи', en: 'Beaumont Oak Natural', ar: 'بلوط بومونت الطبيعي' }, code: 'LW 644-2', color: '#c4a982' },
      { name: { ru: 'Дуб Бомонт Лофт', kz: 'Бомонт Еменi Лофт', en: 'Beaumont Oak Loft', ar: 'بلوط بومونت لوفت' }, code: 'LW 646-2', color: '#8b7a65' },
      { name: { ru: 'Дуб Седан Натуральный', kz: 'Седан Еменi Табиғи', en: 'Sedan Oak Natural', ar: 'بلوط سيدان الطبيعي' }, code: 'LW 724-2', color: '#b89d7a' },
      { name: { ru: 'Дуб Седан Сиеста', kz: 'Седан Еменi Сиеста', en: 'Sedan Oak Siesta', ar: 'بلوط سيدان سيستا' }, code: 'LW 727-2', color: '#7a6552' }
    ],
    superPremium: [
      { name: { ru: 'Дуб Бодега Светлый', kz: 'Бодега Еменi Ашық', en: 'Bodega Oak Light', ar: 'بلوط بوديغا الفاتح' }, code: 'CW 271-3-L', color: '#d4c4a8' },
      { name: { ru: 'Дуб Бодега Натуральный', kz: 'Бодега Еменi Табиғи', en: 'Bodega Oak Natural', ar: 'بلوط بوديغا الطبيعي' }, code: 'CW 274-3-L', color: '#b5a082' },
      { name: { ru: 'Дуб Бодега Золотой', kz: 'Бодега Еменi Алтын', en: 'Bodega Oak Golden', ar: 'بلوط بوديغا الذهبي' }, code: 'CW 275-3-L', color: '#c9a65c' },
      { name: { ru: 'Дуб Бодега Коричневый', kz: 'Бодега Еменi Қоңыр', en: 'Bodega Oak Brown', ar: 'بلوط بوديغا البني' }, code: 'CW 278-SFT-L', color: '#7a5c3d' },
      { name: { ru: 'Скол Камня Грей', kz: 'Тас Жарығы Сұр', en: 'Stone Chip Grey', ar: 'رقائق الحجر الرمادي' }, code: 'NSN 133-N-L', color: '#9a9a9a' },
      { name: { ru: 'Скол Камня Графит', kz: 'Тас Жарығы Графит', en: 'Stone Chip Graphite', ar: 'رقائق الحجر الجرافيت' }, code: 'NSN 136-N-L', color: '#4d4d4d' },
      { name: { ru: 'Травертин Светлый', kz: 'Травертин Ашық', en: 'Travertine Light', ar: 'تراڤرتين فاتح' }, code: 'NSN 111-N-L', color: '#e8dcc8' }
    ],
    luxury: [
      { name: { ru: 'Орех Американский Мокко', kz: 'Американ Жаңғақ Мокко', en: 'American Walnut Mokko', ar: 'الجوز الأمريكي موكا' }, code: 'LW 735-SFT', color: '#5c4033' },
      { name: { ru: 'Орех Американский Шоколад', kz: 'Американ Жаңғақ Шоколад', en: 'American Walnut Chocolate', ar: 'الجوز الأمريكي الشوكولاتة' }, code: 'LW 737-SFT', color: '#3d2817' },
      { name: { ru: 'Орех Американский Натуральный', kz: 'Американ Жаңғақ Табиғи', en: 'American Walnut Natural', ar: 'الجوز الأمريكي الطبيعي' }, code: 'LW 733-SFT', color: '#6b5344' },
      { name: { ru: 'Шпон Орех Натуральный Диагональ', kz: 'Жаңғақ Табиғи Диагональ', en: 'Natural Walnut Veneer Diagonal', ar: 'قشرة الجوز الطبيعي القطري' }, code: 'LW 254-DLSFT', color: '#6b5344' },
      { name: { ru: 'Кварц Лайт Грей', kz: 'Кварц Ашық Сұр', en: 'Quartz Light Grey', ar: 'رمادي فاتح الكوارتز' }, code: 'CRM 122-QZ-L', color: '#c8c8c8' },
      { name: { ru: 'Кварц Грей', kz: 'Кварц Сұр', en: 'Quartz Grey', ar: 'رمادي الكوارتز' }, code: 'CRM 127-QZ-L', color: '#8a8a8a' }
    ]
  };

  const categoryBadges = {
    standard: { ru: 'Стандарт', kz: 'Стандарт', en: 'Standard', ar: 'ستاندرد' },
    premium: { ru: 'Премиум', kz: 'Премиум', en: 'Premium', ar: 'بريميوم' },
    superPremium: { ru: 'Супер-Премиум', kz: 'Супер-Премиум', en: 'Super-Premium', ar: 'سوبر بريميوم' },
    luxury: { ru: 'Люкс', kz: 'Люкс', en: 'Luxury', ar: 'فاخر' },
    cashmere: { ru: 'Кашемир', kz: 'Кашемир', en: 'Cashmere', ar: 'كاشمير' }
  };

  const t = content[language] || content.en || content.ru;

  // Check if selected category is exclusive (none now - all have light background)
  const isExclusiveCategory = false;

  const getFilteredTextures = () => {
    if (activeCategory === 'nomad') {
      // For nomad, we create texture combinations of ornaments and colors
      const textures = [];
      exclusiveCollections.nomad.ornaments.forEach(ornament => {
        exclusiveCollections.nomad.colors.forEach(color => {
          textures.push({
            name: {
              ru: `${ornament.name.ru} - ${color.name.ru}`,
              kz: `${ornament.name.kz} - ${color.name.kz}`,
              en: `${ornament.name.en} - ${color.name.en}`
            },
            code: `${ornament.code}-${color.code}`,
            color: color.color,
            ornament: ornament.name,
            colorName: color.name
          });
        });
      });
      return textures;
    }
    if (activeCategory === 'oriental') {
      return exclusiveCollections.oriental.textures;
    }
    if (activeCategory === 'grey') {
      return exclusiveCollections.grey.textures;
    }
    if (activeCategory === 'all') {
      return Object.entries(catalogData).flatMap(([category, items]) => 
        items.map(item => ({ ...item, category }))
      );
    }
    return catalogData[activeCategory]?.map(item => ({ ...item, category: activeCategory })) || [];
  };

  const filteredTextures = getFilteredTextures();

  const searchFilter = (textures) => {
    if (!searchQuery) return textures;
    const words = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
    return textures.filter(tex => {
      const text = `${tex.name[language] || tex.name.ru || ''} ${tex.code || ''}`.toLowerCase();
      return words.every(w => text.includes(w));
    });
  };

  const handleImageUpload = async (category, textureCode, file) => {
    const sessionId = localStorage.getItem('admin_session');
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setUploadingCode(`${category}_${textureCode}`);
      const res = await fetch(
        `${API_URL}/api/catalog/upload-image?category=${category}&texture_code=${encodeURIComponent(textureCode)}&session_id=${sessionId}`,
        { method: 'POST', body: formData }
      );
      const data = await res.json();
      if (res.ok) {
        setCatalogImages(prev => ({ ...prev, [`${category}_${textureCode}`]: data.image_url }));
      }
    } catch (e) {
      console.error('Upload failed:', e);
    } finally {
      setUploadingCode(null);
    }
  };

  const handleImageDelete = async (category, textureCode) => {
    const sessionId = localStorage.getItem('admin_session');
    try {
      const res = await fetch(
        `${API_URL}/api/catalog/delete-image?category=${category}&texture_code=${encodeURIComponent(textureCode)}&session_id=${sessionId}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        setCatalogImages(prev => {
          const next = { ...prev };
          delete next[`${category}_${textureCode}`];
          return next;
        });
      }
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };

  const openModal = (texture) => {
    setSelectedTexture(texture);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedTexture(null);
    document.body.style.overflow = 'unset';
  };

  // Filter button order - Oriental and Grey before Standard, Nomad after Luxury
  const filterOrder = ['all', 'oriental', 'grey', 'standard', 'cashmere', 'premium', 'superPremium', 'luxury', 'nomad'];

  return (
    <div className="pt-20 bg-[#061d36]" data-testid="catalog-page">
      <SEOHead 
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
      />
      {/* Texture Preview Modal */}
      {selectedTexture && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={closeModal}
          data-testid="texture-modal"
        >
          <div 
            className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="h-64 relative"
              style={(catalogImages[`${selectedTexture.category}_${selectedTexture.code}`] || selectedTexture.image) ? {} : { backgroundColor: selectedTexture.color }}
            >
              {(catalogImages[`${selectedTexture.category}_${selectedTexture.code}`] || selectedTexture.image) && (
                <img 
                  src={catalogImages[`${selectedTexture.category}_${selectedTexture.code}`] || selectedTexture.image}
                  alt={selectedTexture.name[language]}
                  className="w-full h-full object-cover"
                />
              )}
              <button 
                onClick={closeModal}
                className="absolute top-4 right-4 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white transition-colors"
                data-testid="modal-close-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm text-gray-500">APT: {selectedTexture.code}</p>
                {isNew(selectedTexture.newUntil) && <NewBadge size="sm" />}
              </div>
              <h2 className="text-2xl font-bold text-[#061d36] mb-4">
                {selectedTexture.name[language]}
              </h2>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={`https://wa.me/77076792233?text=${encodeURIComponent(
                    language === 'ru' 
                      ? `Здравствуйте! Хочу заказать образец панели:\n\nНазвание: ${selectedTexture.name[language]}\nАртикул: ${selectedTexture.code}\n\nПожалуйста, свяжитесь со мной для уточнения деталей.`
                      : language === 'kz'
                      ? `Сәлеметсіз бе! Панель үлгісіне тапсырыс бергім келеді:\n\nАтауы: ${selectedTexture.name[language]}\nАртикул: ${selectedTexture.code}\n\nМәліметтерді нақтылау үшін маған хабарласыңыз.`
                      : `Hello! I would like to order a panel sample:\n\nName: ${selectedTexture.name[language]}\nArticle: ${selectedTexture.code}\n\nPlease contact me for details.`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeModal}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#1da851] text-white rounded-xl font-semibold transition-all"
                  data-testid="whatsapp-order-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  {t.modal.contact}
                </a>
                <Link
                  to="/contact"
                  onClick={closeModal}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#061d36] hover:bg-[#061d36] hover:text-white text-[#061d36] rounded-xl font-semibold transition-all"
                  data-testid="contact-btn"
                >
                  {t.cta.button}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <section className="py-8 pb-16 bg-[#061d36] text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="catalog-title">
            {t.header.title} <span className="brand-name">{t.header.titleBrand}</span>
          </h1>
          <p className="text-lg text-blue-200 max-w-3xl mx-auto mb-6">{t.header.subtitle}</p>
          
          {/* Download Catalog Button */}
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/pdf/katalog-panelei-gipsovinil.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
              data-testid="download-catalog-btn"
            >
              <Download className="w-5 h-5" />
              {t.downloadCatalog}
            </a>
            <a
              href="/pdf/katalog-ornamentov-gipsovinil.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
              data-testid="download-ornaments-btn"
            >
              <Download className="w-5 h-5" />
              {t.downloadOrnaments}
            </a>
            <a
              href="/pdf/katalog-profilei-gipsovinil.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
              data-testid="download-author-btn"
            >
              <Download className="w-5 h-5" />
              {t.downloadAuthor}
            </a>
          </div>
        </div>
      </section>

      {/* AI Constructor */}
      {/* InteriorConstructor moved to dealer cabinet */}

      {/* Panel Calculator */}
      {/* PanelCalculator moved to dealer cabinet */}

      {/* Filter Section */}
      <section className="pt-12 pb-4 bg-[#F5F5F0]">
        <div className="container mx-auto px-4">
          {/* Category Filter Buttons - Large Rounded Style */}
          <div className="flex flex-wrap justify-center gap-3 mb-0">
            {filterOrder.map((key) => {
              const isExclusive = key === 'oriental' || key === 'grey';
              return (
                <button
                  key={key}
                  onClick={() => setActiveCategory(key)}
                  data-testid={`catalog-filter-${key}`}
                  className={`px-8 py-4 rounded-2xl text-base font-medium transition-all ${
                    activeCategory === key
                      ? 'bg-[#061d36] text-white shadow-lg'
                      : 'bg-[#E8E4E0] text-[#061d36] hover:bg-[#D8D4D0]'
                  } ${isExclusive ? 'relative' : ''}`}
                >
                  {isExclusive && (
                    <Sparkles className="w-4 h-4 inline mr-2 text-amber-500" />
                  )}
                  {t.categories[key]}
                </button>
              );
            })}
            <button
              onClick={() => document.getElementById('design-solutions').scrollIntoView({ behavior: 'smooth' })}
              data-testid="catalog-filter-design"
              className="px-8 py-4 rounded-2xl text-base font-medium transition-all bg-[#E8E4E0] text-[#061d36] hover:bg-[#D8D4D0]"
            >
              {t.designSolutions}
            </button>
          </div>
          <div className="flex justify-center mt-4">
            <input
              type="text"
              data-testid="catalog-search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={language === 'ru' ? 'Поиск по названию или артикулу...' : language === 'kz' ? 'Атауы немесе артикулы бойынша іздеу...' : 'Search by name or article...'}
              className="px-4 py-3 border border-[#061d36]/15 rounded-2xl text-sm w-80 focus:outline-none focus:border-[#8b4444] bg-white"
            />
          </div>
        </div>
      </section>

      {/* Exclusive Collection Header (when exclusive is selected) */}
      {/* Textures Grid */}
      <section className="pt-6 pb-12 bg-[#F5F5F0]">
        <div className="container mx-auto px-4">
          {activeCategory === 'all' ? (
            // Show exclusive collections first, then by category sections
            <>
              {/* Oriental Flavour Collection */}
              {searchFilter(exclusiveCollections.oriental.textures).length > 0 && (
              <div className="mb-12">
                <div className="flex items-center gap-2 mb-6">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <h2 className="text-2xl font-bold text-[#061d36]">
                    {t.categories.oriental}
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                  {searchFilter(exclusiveCollections.oriental.textures).map((texture, idx) => (
                    <ColorCard 
                      key={idx} 
                      texture={texture}
                      language={language}
                      onClick={() => openModal(texture)}
                    />
                  ))}
                </div>
              </div>
              )}

              {/* 5 Shades of Grey Collection */}
              {searchFilter(exclusiveCollections.grey.textures).length > 0 && (
              <div className="mb-12">
                <div className="flex items-center gap-2 mb-6">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <h2 className="text-2xl font-bold text-[#061d36]">
                    {t.categories.grey}
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                  {searchFilter(exclusiveCollections.grey.textures).map((texture, idx) => (
                    <ColorCard 
                      key={idx} 
                      texture={texture}
                      language={language}
                      onClick={() => openModal(texture)}
                    />
                  ))}
                </div>
              </div>
              )}

              {/* Standard Categories */}
              {Object.entries(catalogData).map(([category, items]) => {
                const filtered = searchFilter(items);
                if (filtered.length === 0) return null;
                return (
                <div key={category} className="mb-12">
                  <h2 className="text-2xl font-bold text-[#061d36] mb-6">
                    {categoryBadges[category][language]}
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                    {filtered.map((texture, idx) => (
                      <ColorCard 
                        key={idx} 
                        texture={texture}
                        language={language}
                        onClick={() => openModal({...texture, category})}
                        imageUrl={catalogImages[`${category}_${texture.code}`] || texture.image}
                        isAdmin={isAdmin}
                        onUpload={handleImageUpload}
                        onDelete={handleImageDelete}
                        uploading={uploadingCode === `${category}_${texture.code}`}
                        category={category}
                      />
                    ))}
                  </div>
                </div>
              );
              })}
            </>
          ) : (activeCategory === 'oriental' || activeCategory === 'grey') ? (
            // Show oriental or grey collection textures (light background)
            <div>
              {/* Collection Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-4">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  <span className="text-amber-700 font-medium text-sm uppercase tracking-wider">
                    {language === 'ru' ? 'Эксклюзивная коллекция' : 
                     language === 'kz' ? 'Эксклюзивті коллекция' : 
                     'Exclusive Collection'}
                  </span>
                </div>
                <h2 className="text-3xl font-bold text-[#061d36] mb-3">
                  {activeCategory === 'oriental' 
                    ? exclusiveCollections.oriental.name 
                    : exclusiveCollections.grey.name}
                </h2>
                <p className="text-[#061d36]/70 max-w-2xl mx-auto mb-6">
                  {activeCategory === 'oriental' 
                    ? exclusiveCollections.oriental.description[language]
                    : exclusiveCollections.grey.description[language]
                  }
                </p>
                {/* PDF Download Button for Author's Collection */}
                <a
                  href="/pdf/katalog-panelei-gipsovinil.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-semibold transition-colors"
                  data-testid="author-collection-pdf-download"
                >
                  <Download className="w-5 h-5" />
                  {language === 'ru' ? 'Скачать каталог панелей' : 
                   language === 'kz' ? 'Панельдер каталогын жүктеу' : 
                   'Download Panel Catalog'}
                </a>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                {searchFilter(filteredTextures).map((texture, idx) => (
                  <ColorCard 
                    key={idx} 
                    texture={texture}
                    language={language}
                    onClick={() => openModal(texture)}
                  />
                ))}
              </div>
            </div>
          ) : activeCategory === 'nomad' ? (
            // Nomad Panels - grouped by ornament collections (light background)
            <div className="space-y-12">
              {/* Collection Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-4">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  <span className="text-amber-700 font-medium text-sm uppercase tracking-wider">
                    {language === 'ru' ? 'Казахские орнаменты' : 
                     language === 'kz' ? 'Қазақ ою-өрнектері' : 
                     'Kazakh Ornaments'}
                  </span>
                </div>
                <h2 className="text-3xl font-bold text-[#061d36] mb-3">
                  {exclusiveCollections.nomad.name}
                </h2>
                <p className="text-[#061d36]/70 max-w-2xl mx-auto mb-6">
                  {exclusiveCollections.nomad.description[language]}
                </p>
                {/* PDF Download Button */}
                <a
                  href={exclusiveCollections.nomad.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-semibold transition-colors"
                  data-testid="nomad-pdf-download"
                >
                  <Download className="w-5 h-5" />
                  {language === 'ru' ? 'Скачать каталог орнаментов PDF' : 
                   language === 'kz' ? 'Ою-өрнек каталогын PDF жүктеу' : 
                   'Download Ornaments Catalog PDF'}
                </a>
              </div>
              {exclusiveCollections.nomad.ornaments.filter(ornament => {
                if (!searchQuery) return true;
                const words = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
                const ornText = `${ornament.name[language] || ornament.name.ru} ${ornament.code}`.toLowerCase();
                const anyColorMatch = exclusiveCollections.nomad.colors.some(c => {
                  const ct = `${c.name[language] || c.name.ru} ${c.code}`.toLowerCase();
                  return words.every(w => ornText.includes(w) || ct.includes(w) || `${ornText} ${ct}`.includes(w));
                });
                return anyColorMatch;
              }).map((ornament, ornIdx) => (
                <div key={ornIdx}>
                  <h3 className="text-xl font-bold text-[#061d36] mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 bg-[#061d36] rounded-lg flex items-center justify-center text-white text-sm font-bold">
                      {ornIdx + 1}
                    </span>
                    {ornament.name[language]}
                    <span className="text-gray-400 text-sm font-normal ml-2">({ornament.code})</span>
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-4">
                    {exclusiveCollections.nomad.colors.map((color, colIdx) => {
                      const comboCode = `${ornament.code}-${color.code}`;
                      const imgUrl = catalogImages[`nomad_${comboCode}`];
                      return (
                        <NomadColorCard
                          key={colIdx}
                          color={color}
                          ornament={ornament}
                          comboCode={comboCode}
                          imageUrl={imgUrl}
                          language={language}
                          isAdmin={isAdmin}
                          uploading={uploadingCode === `nomad_${comboCode}`}
                          onUpload={handleImageUpload}
                          onDelete={handleImageDelete}
                          onClick={() => openModal({
                            name: {
                              ru: `${ornament.name.ru} - ${color.name.ru}`,
                              kz: `${ornament.name.kz} - ${color.name.kz}`,
                              en: `${ornament.name.en} - ${color.name.en}`
                            },
                            code: comboCode,
                            color: color.color,
                            category: 'nomad',
                            ornament: ornament.name,
                            colorName: color.name
                          })}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Show filtered standard textures with header
            <div>
              {/* Collection Header for standard categories */}
              {activeCategory !== 'all' && (
                <div className="text-center mb-10">
                  <h2 className="text-3xl font-bold text-[#061d36] mb-3">
                    {t.categories[activeCategory]}
                  </h2>
                  <p className="text-[#061d36]/70 max-w-2xl mx-auto mb-6">
                    {activeCategory === 'standard' && (language === 'ru' ? 'Базовая коллекция высококачественных текстур для любых интерьерных решений' : 
                     language === 'kz' ? 'Кез келген интерьер шешімдері үшін жоғары сапалы текстуралардың базалық коллекциясы' : 
                     'Basic collection of high-quality textures for any interior solutions')}
                    {activeCategory === 'premium' && (language === 'ru' ? 'Премиальные текстуры с улучшенными характеристиками и уникальным дизайном' : 
                     language === 'kz' ? 'Жақсартылған сипаттамалары мен бірегей дизайны бар премиум текстуралар' : 
                     'Premium textures with enhanced characteristics and unique design')}
                    {activeCategory === 'superPremium' && (language === 'ru' ? 'Эксклюзивные решения для требовательных проектов с максимальным качеством' : 
                     language === 'kz' ? 'Максималды сапамен талапты жобалар үшін эксклюзивті шешімдер' : 
                     'Exclusive solutions for demanding projects with maximum quality')}
                    {activeCategory === 'luxury' && (language === 'ru' ? 'Люксовая коллекция для самых изысканных интерьеров' : 
                     language === 'kz' ? 'Ең талғампаз интерьерлер үшін люкс коллекциясы' : 
                     'Luxury collection for the most exquisite interiors')}
                  </p>
                  {/* Download Catalog Button */}
                  <a
                    href="/pdf/katalog-panelei-gipsovinil.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-semibold transition-colors"
                    data-testid="textures-pdf-download"
                  >
                    <Download className="w-5 h-5" />
                    {language === 'ru' ? 'Скачать каталог панелей PDF' : 
                     language === 'kz' ? 'Панельдер каталогын PDF жүктеу' : 
                     'Download Panels Catalog PDF'}
                  </a>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                {searchFilter(filteredTextures).map((texture, idx) => (
                  <ColorCard 
                    key={idx} 
                    texture={texture}
                    language={language}
                    onClick={() => openModal(texture)}
                    imageUrl={catalogImages[`${activeCategory}_${texture.code}`] || texture.image}
                    isAdmin={isAdmin}
                    onUpload={handleImageUpload}
                    onDelete={handleImageDelete}
                    uploading={uploadingCode === `${activeCategory}_${texture.code}`}
                    category={activeCategory}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Design Solutions */}
      <section className="bg-[#061d36]" id="design-solutions" data-testid="design-solutions">
        <div className="space-y-0">
          {Array.from({length: 16}, (_, i) => i + 17).map(num => (
            <img
              key={num}
              src={`/images/catalog/slide_${num}.jpg`}
              alt={`${t.designSolutions} ${num - 16}`}
              className="w-full h-auto block"
              loading="lazy"
            />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pt-16 py-8 pb-16 bg-[#061d36]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">{t.cta.title}</h2>
          <p className="text-blue-200 mb-8 max-w-2xl mx-auto">{t.cta.subtitle}</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-semibold transition-all"
          >
            {t.cta.button}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Nomad Color Card Component with admin upload support
const NomadColorCard = ({ color, ornament, comboCode, imageUrl, language, isAdmin, uploading, onUpload, onDelete, onClick }) => {
  const fileInputRef = useRef(null);
  
  return (
    <div 
      className="bg-white border border-gray-200 hover:shadow-lg rounded-xl overflow-hidden cursor-pointer transition-all group"
      onClick={onClick}
      data-testid={`nomad-card-${comboCode}`}
    >
      <div 
        className="aspect-square relative"
        style={imageUrl ? {} : { backgroundColor: color.color }}
      >
        {imageUrl && (
          <img 
            src={imageUrl} 
            alt={`${ornament.name[language]} - ${color.name[language]}`} 
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        )}
        {isAdmin && (
          <div className="absolute top-2 right-2 z-10 flex gap-1" onClick={e => e.stopPropagation()}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files[0]) onUpload('nomad', comboCode, e.target.files[0]);
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-1.5 bg-white/90 hover:bg-white rounded-lg shadow transition-all"
              data-testid={`nomad-upload-${comboCode}`}
              title="Загрузить фото"
            >
              {uploading ? (
                <div className="w-4 h-4 border-2 border-[#061d36] border-t-transparent rounded-full animate-spin" />
              ) : (
                <Upload className="w-4 h-4 text-[#061d36]" />
              )}
            </button>
            {imageUrl && (
              <button
                onClick={() => onDelete('nomad', comboCode)}
                className="p-1.5 bg-white/90 hover:bg-red-50 rounded-lg shadow transition-all"
                data-testid={`nomad-delete-${comboCode}`}
                title="Удалить фото"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="p-3 text-center">
        <p className="text-[#061d36] text-sm font-medium truncate">{color.name[language]}</p>
        <p className="text-gray-400 text-xs">{color.code}</p>
      </div>
    </div>
  );
};

// Color Card Component
const ColorCard = ({ texture, language, onClick, dark = false, imageUrl, isAdmin, onUpload, onDelete, uploading, category }) => {
  const fileInputRef = useRef(null);
  
  return (
    <div 
      className={`rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all group cursor-pointer ${
        dark ? 'bg-white/10 border border-white/10 hover:bg-white/20' : 'bg-white'
      }`}
      onClick={onClick}
      data-testid={`texture-card-${texture.code}`}
    >
      {/* Color Preview / Image */}
      <div 
        className="aspect-square relative flex items-end justify-start p-4"
        style={imageUrl ? {} : { backgroundColor: texture.color }}
      >
        {imageUrl && (
          <img 
            src={imageUrl} 
            alt={texture.name[language]} 
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        )}
        <span className={`relative z-10 text-sm font-bold ${
          imageUrl ? 'text-white drop-shadow-lg' : isLightColor(texture.color) ? 'text-[#061d36]' : 'text-white'
        }`}>
          APT: {texture.code}
        </span>

        {/* NEW badge (auto-hides after texture.newUntil) */}
        {isNew(texture.newUntil) && (
          <NewBadge className="absolute top-2 left-2 z-10" size="md" />
        )}
        
        {/* Admin upload/delete buttons */}
        {isAdmin && (
          <div className="absolute top-2 right-2 z-10 flex gap-1" onClick={e => e.stopPropagation()}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files[0]) onUpload(category, texture.code, e.target.files[0]);
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-1.5 bg-white/90 hover:bg-white rounded-lg shadow transition-all"
              data-testid={`upload-btn-${texture.code}`}
              title="Загрузить фото"
            >
              {uploading ? (
                <div className="w-4 h-4 border-2 border-[#061d36] border-t-transparent rounded-full animate-spin" />
              ) : (
                <Upload className="w-4 h-4 text-[#061d36]" />
              )}
            </button>
            {imageUrl && (
              <button
                onClick={() => onDelete(category, texture.code)}
                className="p-1.5 bg-white/90 hover:bg-red-50 rounded-lg shadow transition-all"
                data-testid={`delete-btn-${texture.code}`}
                title="Удалить фото"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            )}
          </div>
        )}
      </div>
      
      {/* Info */}
      <div className="p-4">
        <h3 className={`font-semibold text-base leading-tight ${
          dark ? 'text-white group-hover:text-amber-300' : 'text-[#061d36] group-hover:text-blue-600'
        } transition-colors`}>
          {texture.name[language]}
        </h3>
      </div>
    </div>
  );
};

// Helper function to determine if a color is light or dark
const isLightColor = (color) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128;
};

export default CatalogPage;
